import {
    ResizeObserverTarget,
    useResizeObserver,
} from '@/hooks/use-resize-observer';
import { useCallback, useState } from 'react';

export interface ContainerSize {
    width: number;
    height: number;
}

const ZERO_SIZE: ContainerSize = { width: 0, height: 0 };

/**
 * Tracks a container's own rendered size, measured before paint and kept in
 * sync as the element resizes.
 *
 * Reports the padding box (`clientWidth`/`clientHeight`), and stays `{0, 0}`
 * until the first measurement — on the server, and before `target` mounts. If
 * the target later goes away, the last measured size is retained rather than
 * reset, since a ref target's removal can't be observed at all (see
 * `useResizeObserver`) and reporting it for only one of the two target kinds
 * would be less predictable than one rule. Returns the same object across
 * resizes that don't change either dimension, so consumers don't re-render on
 * them.
 *
 * @param target Element to measure, as a ref object or the element itself.
 */
export function useContainerSize(target: ResizeObserverTarget): ContainerSize {
    const [size, setSize] = useState<ContainerSize>(ZERO_SIZE);

    const measure = useCallback((element: Element) => {
        const { clientWidth: width, clientHeight: height } = element;
        setSize((current) =>
            current.width === width && current.height === height
                ? current
                : { width, height }
        );
    }, []);

    useResizeObserver(target, measure);

    return size;
}
